import { memo, ReactNode } from "react";
import { LinearProgress, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { faBomb, faRulerVertical, faTriangleExclamation, faWrench } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";

import { PatternStatus } from "../../../DTOs/DrillBlast/PatternLifecycleDTO";
import { METER_LABELS } from "../../../DTOs/DrillBlast/RigMeterDTO";
import { RootState } from "../../../state/store";
import { blastPatternTarget, drillingRigTarget } from "../../../utilities/helpers/dashboardLinks";
import { AccessLink } from "../../AccessLink/AccessLink";
import { Pill } from "../../DrillBlast/Indicators";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { blastsTodayQuery, drillBlastQuery, openMisfiresQuery, serviceDueQuery } from "./dashboardQueries";

const SERVICE_SOON_HOURS = 25;
const RIGS_SHOWN = 5;

const ROW = "rounded-xl border border-theme-border bg-theme-surface px-3 py-2 flex items-start gap-3 transition";
const LINKED = "cursor-pointer hover:border-theme-table-heading-contrast hover:bg-theme-surface-2";

const Section = ({ icon, title, children }: { icon: FontAwesomeIconProps["icon"]; title: string; children: ReactNode }) => (
  <div className="flex flex-col gap-2">
    <Typography variant="body2" className="!font-bold !text-theme-text flex items-center gap-2">
      <FontAwesomeIcon icon={icon} className="text-theme-text-muted" />{title}
    </Typography>
    {children}
  </div>
);

const number = (value: number | null | undefined, digits = 0) =>
  value == null ? "-" : value.toLocaleString(undefined, { maximumFractionDigits: digits });

export const DrillBlastTodayCard: React.FC = memo(() => {
  const { isFullScreen } = useDashboardCard();
  const { selectedDate } = useShiftTimes();
  const category = useSelector((state: RootState) => state.dashboard.targetCategory);
  const enabled = selectedDate.isValid;

  const { data: drillBlast = null, isLoading: drillBlastLoading } = useQuery({ ...drillBlastQuery(selectedDate, category), enabled });
  const { data: blasts = null, isLoading: blastsLoading } = useQuery({ ...blastsTodayQuery(selectedDate), enabled });
  const { data: misfires, isLoading: misfiresLoading } = useQuery({ ...openMisfiresQuery, retry: false });
  const { data: serviceDue = null, isLoading: serviceLoading } = useQuery(serviceDueQuery);
  const loading = !enabled || drillBlastLoading || blastsLoading || misfiresLoading || serviceLoading;

  const drilled = drillBlast?.entries.find(e => e.key === "drill-metres");
  const metres = drilled?.actual ?? null;
  const metresTarget = drilled?.target ?? null;
  const dueRigs = (serviceDue ?? [])
    .filter(r => r.hoursToService != null)
    .sort((a, b) => a.hoursToService! - b.hoursToService!)
    .slice(0, RIGS_SHOWN);
  const nothing = metres == null && !blasts?.length && !misfires?.length && dueRigs.length === 0;

  if (loading) {
    return <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col" />;
  }

  if (nothing) {
    return (
      <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
        <DashboardCardEmptyState isFullScreen={isFullScreen} icon={faBomb} title="Nothing to show"
          subtitle="No drilling, blasts, open misfires or rigs coming due a service for the selected date." />
      </Paper>
    );
  }

  return (
    <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
      <div className="animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-3 overflow-y-auto">
        {metres != null && (
          <Section icon={faRulerVertical} title="Drill metres today">
            <div className={ROW}>
              <div className="flex flex-col gap-1 w-full">
                <Typography variant="body2" className="!text-theme-text">
                  <strong>{number(metres)} m</strong>
                  {metresTarget != null && <span className="text-theme-text-muted"> of {number(metresTarget)} m {category.toLowerCase()}</span>}
                </Typography>
                {metresTarget != null && metresTarget > 0 && (
                  <LinearProgress variant="determinate" value={Math.min(100, metres / metresTarget * 100)}
                    color={metres >= metresTarget ? "success" : "warning"} />
                )}
              </div>
            </div>
          </Section>
        )}

        {!!misfires?.length && (
          <Section icon={faTriangleExclamation} title="Open misfires">
            {misfires.map(m => (
              <AccessLink key={m.patternBlastId} target={blastPatternTarget(m.patternBlastId)} className={ROW} linkedClassName={LINKED}>
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-theme-danger mt-1" />
                <div className="text-left">
                  <Typography variant="body2" className="!font-bold !text-theme-text">
                    {m.reference} · {m.count} {m.count === 1 ? "misfire" : "misfires"}
                  </Typography>
                  {m.holes.length > 0 && <Typography variant="caption" className="!text-theme-text-muted">Holes {m.holes.join(", ")}</Typography>}
                </div>
              </AccessLink>
            ))}
          </Section>
        )}

        {!!blasts?.length && (
          <Section icon={faBomb} title="Blasts today">
            {blasts.map(b => (
              <AccessLink key={b.patternBlastId} target={blastPatternTarget(b.patternBlastId)} className={ROW} linkedClassName={LINKED}>
                <div className="text-left flex flex-col gap-1 w-full">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Typography variant="body2" className="!font-bold !text-theme-text">{b.reference}</Typography>
                    <Pill tone={b.firedAt ? "neutral" : b.status === PatternStatus.Charged ? "danger" : "warning"}>
                      {b.firedAt ? `Fired ${b.firedAt.toFormat("HH:mm")}` : b.plannedFireAt ? `Fires ${b.plannedFireAt.toFormat("HH:mm")}` : PatternStatus[b.status]}
                    </Pill>
                  </div>
                  <Typography variant="caption" className="!text-theme-text-muted">
                    {[b.pitStage ?? b.pit, b.tonnes > 0 ? `${number(b.tonnes)} t` : null, !b.firedAt && b.exclusionZoneM ? `exclusion ${b.exclusionZoneM} m` : null]
                      .filter(Boolean).join(" · ")}
                  </Typography>
                </div>
              </AccessLink>
            ))}
          </Section>
        )}

        {dueRigs.length > 0 && (
          <Section icon={faWrench} title="Rigs coming due a service">
            {dueRigs.map(r => (
              <AccessLink key={`${r.plantId}-${r.meterType}`} target={drillingRigTarget(r.plantId)} className={ROW} linkedClassName={LINKED}>
                <div className="text-left flex flex-wrap items-center justify-between gap-2 w-full">
                  <Typography variant="body2" className="!text-theme-text">
                    <strong>{r.plantRef}</strong> <span className="text-theme-text-muted">{METER_LABELS[r.meterType].toLowerCase()}</span>
                  </Typography>
                  <Pill tone={r.hoursToService! <= 0 ? "danger" : r.hoursToService! <= SERVICE_SOON_HOURS ? "warning" : "neutral"}>
                    {r.hoursToService! <= 0 ? `${number(-r.hoursToService!, 1)} h overdue` : `${number(r.hoursToService, 1)} h to service`}
                  </Pill>
                </div>
              </AccessLink>
            ))}
          </Section>
        )}
      </div>
    </Paper>
  );
});
